/**
 * Photometric calibration: mapping relative (pipeline) luminance to physical
 * units from user-supplied reference points or constants.
 */

export type CalibrationMode = 'none' | 'scale' | 'twoPoint' | 'poly' | 'lut';

export interface CalPoint {
  /** Relative value measured by Aperto (uncalibrated Y). */
  measured: number;
  /** Known reference value in the calibrated unit (e.g. from a spot luminance meter). */
  reference: number;
  label?: string;
}

export interface PhotometricCalibration {
  mode: CalibrationMode;
  /** Unit label for calibrated values, e.g. 'cd/m²'. */
  unit: string;
  /** 'scale' mode: value = scale * Y. */
  scale: number;
  /** 'twoPoint' mode: value = gain * Y + offset. */
  gain: number;
  offset: number;
  polyDegree: number;
  points: CalPoint[];
  /**
   * When true, 'scale' and 'twoPoint' parameters are fitted from `points`
   * instead of being entered by hand.
   */
  fitFromPoints: boolean;
}

export const DEFAULT_CALIBRATION: PhotometricCalibration = {
  mode: 'none',
  unit: 'cd/m²',
  scale: 1,
  gain: 1,
  offset: 0,
  polyDegree: 2,
  points: [],
  fitFromPoints: false,
};

export interface CalibrationFit {
  /** Evaluates the calibration function. */
  f: (y: number) => number;
  /** Polynomial coefficients, lowest order first (for scale/twoPoint/poly). */
  coeffs: number[];
  /** Residuals (fitted - reference) at each point. */
  residuals: number[];
  rmse: number;
  r2: number;
  description: string;
  error?: string;
}

/** Solve a small dense linear system by Gaussian elimination with partial pivoting. */
export function solveLinear(A: number[][], b: number[]): number[] {
  const n = b.length;
  const M = A.map((row, i) => [...row, b[i]]);
  for (let c = 0; c < n; c++) {
    let p = c;
    for (let r = c + 1; r < n; r++) if (Math.abs(M[r][c]) > Math.abs(M[p][c])) p = r;
    if (Math.abs(M[p][c]) < 1e-300) throw new Error('Singular system');
    [M[c], M[p]] = [M[p], M[c]];
    for (let r = c + 1; r < n; r++) {
      const f = M[r][c] / M[c][c];
      for (let k = c; k <= n; k++) M[r][k] -= f * M[c][k];
    }
  }
  const x = new Array(n).fill(0);
  for (let r = n - 1; r >= 0; r--) {
    let s = M[r][n];
    for (let k = r + 1; k < n; k++) s -= M[r][k] * x[k];
    x[r] = s / M[r][r];
  }
  return x;
}

/** Least-squares polynomial fit y = sum c_k x^k, k = 0..degree. Inputs are scaled for conditioning. */
export function polyFit(xs: number[], ys: number[], degree: number): number[] {
  const n = degree + 1;
  if (xs.length < n) throw new Error(`Need at least ${n} points for degree ${degree}`);
  const sx = Math.max(...xs.map(Math.abs)) || 1;
  const A: number[][] = Array.from({ length: n }, () => new Array(n).fill(0));
  const b = new Array(n).fill(0);
  for (let i = 0; i < xs.length; i++) {
    const t = xs[i] / sx;
    const pw: number[] = [1];
    for (let k = 1; k < 2 * n; k++) pw.push(pw[k - 1] * t);
    for (let r = 0; r < n; r++) {
      b[r] += ys[i] * pw[r];
      for (let c = 0; c < n; c++) A[r][c] += pw[r + c];
    }
  }
  const cs = solveLinear(A, b);
  return cs.map((c, k) => c / Math.pow(sx, k));
}

export function polyEval(c: number[], x: number): number {
  let s = 0;
  for (let k = c.length - 1; k >= 0; k--) s = s * x + c[k];
  return s;
}

/** Piecewise-linear interpolation through sorted (x, y) knots with linear extrapolation. */
export function makeLut(points: CalPoint[]): (y: number) => number {
  const p = [...points].sort((a, b) => a.measured - b.measured);
  if (p.length === 1) {
    const k = p[0].reference / p[0].measured;
    return (y) => y * k;
  }
  return (y) => {
    let i = 0;
    if (y <= p[0].measured) i = 0;
    else if (y >= p[p.length - 1].measured) i = p.length - 2;
    else while (i < p.length - 2 && y > p[i + 1].measured) i++;
    const a = p[i];
    const b = p[i + 1];
    const t = (y - a.measured) / (b.measured - a.measured);
    return a.reference + t * (b.reference - a.reference);
  };
}

function stats(points: CalPoint[], f: (y: number) => number) {
  const residuals = points.map((p) => f(p.measured) - p.reference);
  const n = points.length;
  const rmse = n ? Math.sqrt(residuals.reduce((s, r) => s + r * r, 0) / n) : 0;
  const mean = n ? points.reduce((s, p) => s + p.reference, 0) / n : 0;
  const ssTot = points.reduce((s, p) => s + (p.reference - mean) ** 2, 0);
  const ssRes = residuals.reduce((s, r) => s + r * r, 0);
  const r2 = ssTot > 0 ? 1 - ssRes / ssTot : ssRes === 0 ? 1 : 0;
  return { residuals, rmse, r2 };
}

/** Build the calibration function for the current settings (fitting if required). */
export function buildCalibration(cal: PhotometricCalibration): CalibrationFit {
  const pts = cal.points.filter((p) => Number.isFinite(p.measured) && Number.isFinite(p.reference));
  const identity: CalibrationFit = { f: (y) => y, coeffs: [0, 1], residuals: [], rmse: 0, r2: 1, description: 'identity' };
  const fail = (error: string): CalibrationFit => ({ ...identity, error, description: 'identity (calibration invalid)' });
  try {
    switch (cal.mode) {
      case 'none':
        return identity;
      case 'scale': {
        let k = cal.scale;
        if (cal.fitFromPoints) {
          if (pts.length < 1) return fail('Scale fit needs at least 1 reference point');
          // Least squares through the origin: k = sum(m r) / sum(m^2)
          const num = pts.reduce((s, p) => s + p.measured * p.reference, 0);
          const den = pts.reduce((s, p) => s + p.measured * p.measured, 0);
          if (!(den > 0)) return fail('Reference points have zero measured value');
          k = num / den;
        }
        const f = (y: number) => k * y;
        return { f, coeffs: [0, k], ...stats(pts, f), description: `${fmt(k)} × Y` };
      }
      case 'twoPoint': {
        let g = cal.gain;
        let o = cal.offset;
        if (cal.fitFromPoints) {
          if (pts.length < 2) return fail('Offset + gain fit needs at least 2 reference points');
          [o, g] = polyFit(
            pts.map((p) => p.measured),
            pts.map((p) => p.reference),
            1,
          );
        }
        const f = (y: number) => g * y + o;
        return { f, coeffs: [o, g], ...stats(pts, f), description: `${fmt(g)} × Y ${o >= 0 ? '+' : '−'} ${fmt(Math.abs(o))}` };
      }
      case 'poly': {
        const deg = Math.max(1, Math.min(5, Math.round(cal.polyDegree)));
        if (pts.length < deg + 1) return fail(`Polynomial of degree ${deg} needs at least ${deg + 1} reference points`);
        const c = polyFit(
          pts.map((p) => p.measured),
          pts.map((p) => p.reference),
          deg,
        );
        const f = (y: number) => polyEval(c, y);
        return { f, coeffs: c, ...stats(pts, f), description: c.map((v, k) => `${fmt(v)}·Y^${k}`).join(' + ') };
      }
      case 'lut': {
        if (pts.length < 1) return fail('Lookup table needs at least 1 reference point');
        const measured = new Set(pts.map((p) => p.measured));
        if (measured.size !== pts.length) return fail('Lookup table points must have distinct measured values');
        const f = makeLut(pts);
        return { f, coeffs: [], ...stats(pts, f), description: `piecewise linear through ${pts.length} point(s)` };
      }
    }
  } catch (e) {
    return fail((e as Error).message);
  }
}

function fmt(v: number): string {
  return Number.isFinite(v) ? Number(v.toPrecision(6)).toString() : String(v);
}
